import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  useTheme,
} from '@mui/material';
import { colors } from '../theme';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DescriptionIcon from '@mui/icons-material/Description';
import BuildIcon from '@mui/icons-material/Build';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

function WorkflowSection() {
  const theme = useTheme();

  const steps = [
    {
      label: 'Site Visit',
      icon: <SearchIcon sx={{ fontSize: 40 }} />,
      description:
        'Our team visits your location to assess space, electrical capacity, and infrastructure requirements.',
      duration: '1-3 days',
    },
    {
      label: 'Technical Assessment',
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      description:
        'Detailed technical evaluation including load analysis, solar potential, and ESS integration feasibility.',
      duration: '3-5 days',
    },
    {
      label: 'Proposal & Planning',
      icon: <DescriptionIcon sx={{ fontSize: 40 }} />,
      description:
        'Comprehensive proposal with system design, cost breakdown, ROI analysis, and project timeline.',
      duration: '7-10 days',
    },
    {
      label: 'Installation',
      icon: <BuildIcon sx={{ fontSize: 40 }} />,
      description:
        'Professional installation of chargers, solar panels (if applicable), ESS, and supporting infrastructure.',
      duration: '4-6 weeks',
    },
    {
      label: 'Testing & Commissioning',
      icon: <FactCheckIcon sx={{ fontSize: 40 }} />,
      description:
        'Rigorous testing, safety checks, compliance verification, and system optimization.',
      duration: '1-2 weeks',
    },
    {
      label: 'Go-Live & Handover',
      icon: <RocketLaunchIcon sx={{ fontSize: 40 }} />,
      description:
        'Final handover, staff training, documentation delivery, and ongoing support setup.',
      duration: '1-2 days',
    },
  ];

  return (
    <Box
      id="workflow"
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: '#f8f8f8',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 800,
              mb: 2,
              color: colors.black,
            }}
          >
            Project{' '}
            <Box component="span" sx={{ color: colors.green }}>
              Workflow
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              maxWidth: '700px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.2rem' },
            }}
          >
            From initial consultation to go-live - a streamlined process
          </Typography>
        </Box>

        {/* Compact Horizontal Timeline */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {steps.map((step, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  height: '100%',
                  borderRadius: 3,
                  border: `2px solid ${
                    index % 2 === 0 ? colors.green : colors.yellow
                  }40`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    border: `2px solid ${
                      index % 2 === 0 ? colors.green : colors.yellow
                    }`,
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px ${
                      index % 2 === 0 ? colors.green : colors.yellow
                    }20`,
                  },
                }}
              >
                {/* Step Number & Icon */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: `${
                        index % 2 === 0 ? colors.green : colors.yellow
                      }20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: index % 2 === 0 ? colors.green : colors.black,
                      border: `2px solid ${
                        index % 2 === 0 ? colors.green : colors.yellow
                      }`,
                      flexShrink: 0,
                    }}
                  >
                    {React.cloneElement(step.icon, { sx: { fontSize: 28 } })}
                  </Box>
                  <Box
                    sx={{
                      bgcolor: index % 2 === 0 ? colors.green : colors.yellow,
                      color: index % 2 === 0 ? colors.white : colors.black,
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </Box>
                </Box>

                {/* Step Title */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: colors.black,
                    fontSize: '1.1rem',
                    mb: 1,
                  }}
                >
                  {step.label}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    color: '#666',
                    mb: 1.5,
                    lineHeight: 1.5,
                    fontSize: '0.9rem',
                  }}
                >
                  {step.description}
                </Typography>

                {/* Duration */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    bgcolor:
                      index % 2 === 0 ? `${colors.green}15` : `${colors.yellow}30`,
                  }}
                >
                  <Typography
                    sx={{
                      color: index % 2 === 0 ? colors.green : colors.black,
                      fontWeight: 600,
                      fontSize: '0.8rem',
                    }}
                  >
                    ⏱️ {step.duration}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Total Timeline */}
        <Box
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${colors.black} 0%, ${colors.darkGreen} 100%)`,
            textAlign: 'center',
            boxShadow: `0 12px 40px ${colors.green}30`,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: colors.yellow,
              fontWeight: 800,
              mb: 0.5,
              fontSize: { xs: '1.3rem', md: '1.75rem' },
            }}
          >
            Typical Lead Time: 2-4 Months
          </Typography>
          <Typography
            sx={{
              color: colors.white,
              fontSize: { xs: '0.95rem', md: '1rem' },
              opacity: 0.95,
            }}
          >
            From initial consultation to full operational deployment
          </Typography>
        </Box>

        {/* Additional Info */}
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          {[
            { label: 'Dedicated PM', icon: '👨‍💼' },
            { label: 'Progress Updates', icon: '📊' },
            { label: 'Quality Assurance', icon: '✅' },
            { label: 'Post-Install Support', icon: '🛠️' },
          ].map((item, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                border: `2px solid ${colors.green}40`,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                transition: 'all 0.3s ease',
                '&:hover': {
                  border: `2px solid ${colors.green}`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 16px ${colors.green}20`,
                },
              }}
            >
              <Typography sx={{ fontSize: '1.2rem' }}>{item.icon}</Typography>
              <Typography
                sx={{
                  color: colors.black,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                }}
              >
                {item.label}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default WorkflowSection;
